import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { AnnotationThread } from './AnnotationThread';
import type {
  BlockName,
  ConversationTurn,
  FixtureData,
  LangfuseEvaluationStatus,
  SnowflakeValidationStatus,
} from './types';
import { useFixtureReviews } from './useAnnotations';
import {
  formatExecutionTime,
  formatTimeDelta,
  NotesLink,
  VALIDATION_STATUS_CHIP,
} from './ValidationDashboard';

SyntaxHighlighter.registerLanguage('sql', sql);

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  expressible: 'success',
  gap: 'warning',
  validation_error: 'error',
  pending_ir: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  expressible: 'Expressible',
  gap: 'Gap',
  validation_error: 'Validation Error',
  pending_ir: 'Pending IR',
};

const ROLE_COLORS: Record<string, string> = {
  inclusion: 'blue.800',
  exclusion: 'red.800',
  demographic: 'purple.800',
  index_date: 'teal.800',
  outcome: 'orange.900',
  covariate: 'bluegrey.800',
  subgroup: 'deeppurple.800',
  observability: 'bluegrey.700',
};

const COMPLEXITY_BG: Record<string, string> = {
  simple: 'green.50',
  moderate: 'blue.50',
  complex: 'orange.50',
  very_complex: 'pink.50',
};

const COMPLEXITY_FG: Record<string, string> = {
  simple: 'green.800',
  moderate: 'blue.800',
  complex: 'orange.900',
  very_complex: 'red.800',
};

function stripNulls(obj: unknown): unknown {
  if (obj === null || obj === undefined) return undefined;
  if (Array.isArray(obj)) return obj.map(stripNulls);
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const stripped = stripNulls(v);
      if (stripped !== undefined) result[k] = stripped;
    }
    return result;
  }
  return obj;
}

function valueSummary(val: unknown): string {
  if (Array.isArray(val)) return `[ ${val.length} items ]`;
  if (val !== null && typeof val === 'object') {
    const keys = Object.keys(val as Record<string, unknown>);
    return `{ ${keys.length} ${keys.length === 1 ? 'key' : 'keys'} }`;
  }
  return JSON.stringify(val);
}

// TODO: all colors below in bracketStyle, formatPrimitive, CollapsibleNode, sqlHighlightStyle, and diffStyles are code-editor/syntax-highlight colors with no VDS token — needs designer input (Barry Richardson)
const bracketStyle = { color: '#abb2bf', fontFamily: 'monospace', fontSize: '1.5rem' } as const;

function isCollapsible(val: unknown): boolean {
  return (
    (Array.isArray(val) && val.length > 0) ||
    (val !== null &&
      typeof val === 'object' &&
      Object.keys(val as Record<string, unknown>).length > 0)
  );
}

function formatPrimitive(val: unknown): React.ReactNode {
  if (typeof val === 'string') {
    return (
      <Typography
        component="span"
        sx={{ color: '#98c379', fontFamily: 'monospace', fontSize: '1.5rem' }}
      >
        &quot;{val}&quot;
      </Typography>
    );
  }
  if (typeof val === 'number') {
    return (
      <Typography
        component="span"
        sx={{ color: '#d19a66', fontFamily: 'monospace', fontSize: '1.5rem' }}
      >
        {String(val)}
      </Typography>
    );
  }
  if (typeof val === 'boolean' || val === null) {
    return (
      <Typography
        component="span"
        sx={{ color: '#56b6c2', fontFamily: 'monospace', fontSize: '1.5rem' }}
      >
        {String(val)}
      </Typography>
    );
  }
  return (
    <Typography
      component="span"
      sx={{ color: '#abb2bf', fontFamily: 'monospace', fontSize: '1.5rem' }}
    >
      {JSON.stringify(val)}
    </Typography>
  );
}

function CollapsibleNode({
  label,
  value,
  depth,
  defaultExpanded = true,
}: {
  label: React.ReactNode;
  value: unknown;
  depth: number;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const canCollapse = isCollapsible(value);

  if (!canCollapse) {
    return (
      <Box sx={{ py: 0.25 }}>
        {label}
        <Typography
          component="span"
          sx={{ color: '#abb2bf', fontFamily: 'monospace', fontSize: '1.5rem' }}
        >
          :{' '}
        </Typography>
        {formatPrimitive(value)}
      </Box>
    );
  }

  const isArray = Array.isArray(value);
  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';
  const children: [string, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);

  return (
    <Box>
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
          cursor: 'pointer',
          '&:hover': { background: '#293548' },
          userSelect: 'none',
        }}
      >
        {expanded ? (
          <ExpandMoreIcon sx={{ color: '#abb2bf', fontSize: '1.5rem', mr: 0.5 }} />
        ) : (
          <ChevronRightIcon sx={{ color: '#abb2bf', fontSize: '1.5rem', mr: 0.5 }} />
        )}
        {label}
        <Typography component="span" sx={{ ...bracketStyle, ml: 0.5 }}>
          :{' '}
        </Typography>
        {expanded ? (
          <Typography component="span" sx={bracketStyle}>
            {openBracket}
          </Typography>
        ) : (
          <Typography
            component="span"
            sx={{ color: '#7f8694', fontFamily: 'monospace', fontSize: '1.5rem' }}
          >
            {valueSummary(value)}
          </Typography>
        )}
      </Box>
      {expanded && (
        <>
          <Box
            sx={{
              ml: 1.5,
              pl: 2,
              borderLeft: '2px solid #3b4557',
            }}
          >
            {children.map(([childKey, childVal]) => {
              const childLabel = isArray ? (
                <Typography
                  component="span"
                  sx={{ color: '#7f8694', fontFamily: 'monospace', fontSize: '1.5rem' }}
                >
                  {childKey}
                </Typography>
              ) : (
                <Typography
                  component="span"
                  sx={{ color: '#e06c75', fontFamily: 'monospace', fontSize: '1.5rem' }}
                >
                  &quot;{childKey}&quot;
                </Typography>
              );
              return (
                <CollapsibleNode
                  key={childKey}
                  label={childLabel}
                  value={childVal}
                  depth={depth + 1}
                  defaultExpanded={defaultExpanded}
                />
              );
            })}
          </Box>
          <Box sx={{ py: 0.25 }}>
            <Typography component="span" sx={bracketStyle}>
              {closeBracket}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

function CollapsibleJsonTree({
  data,
  defaultExpanded = true,
  changedBlocks,
}: {
  data: unknown;
  defaultExpanded?: boolean;
  changedBlocks?: string[];
}) {
  const stripped = useMemo(() => stripNulls(data), [data]);
  const changedSet = useMemo(
    () => (changedBlocks ? new Set(changedBlocks) : undefined),
    [changedBlocks]
  );

  if (!stripped || typeof stripped !== 'object') {
    return (
      <Box
        sx={{
          background: '#1e293b',
          borderRadius: '6px',
          p: 2,
          fontFamily: 'monospace',
        }}
      >
        {formatPrimitive(stripped)}
      </Box>
    );
  }

  const isArray = Array.isArray(stripped);
  const entries: [string, unknown][] = isArray
    ? (stripped as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(stripped as Record<string, unknown>);

  // Determine which top-level keys have changed blocks
  const isKeyChanged = (key: string): boolean => {
    if (!changedSet) return false;
    // Direct match or prefix match (e.g. "criteria" matches "criteria", "av0:logic" matches block keys)
    for (const block of changedSet) {
      if (
        block === key ||
        block.startsWith(`${key}:`) ||
        key === 'analyticalVariables' ||
        key === 'criteria'
      ) {
        // Check if any changed block relates to this key
        if (block === key || block === 'criteria') return true;
        if (
          key === 'analyticalVariables' &&
          (block.includes('logic') ||
            block.includes('window') ||
            block.includes('aggregation') ||
            block.includes('output'))
        )
          return true;
      }
    }
    return false;
  };

  return (
    <Box
      sx={{
        background: '#1e293b',
        borderRadius: '6px',
        overflow: 'hidden',
        fontFamily: 'monospace',
        py: 0.5,
      }}
    >
      {entries.map(([key, val]) => {
        const changed = isKeyChanged(key);
        const label = isArray ? (
          <Typography
            component="span"
            sx={{ color: '#7f8694', fontFamily: 'monospace', fontSize: '1.5rem' }}
          >
            {key}
          </Typography>
        ) : (
          <Typography
            component="span"
            sx={{ color: '#e06c75', fontFamily: 'monospace', fontSize: '1.5rem' }}
          >
            &quot;{key}&quot;
          </Typography>
        );
        return (
          <Box
            key={key}
            sx={{
              borderLeft: changedSet ? `3px solid ${changed ? '#4caf50' : 'transparent'}` : 'none',
              pl: changedSet ? 1 : 0,
              opacity: changedSet && !changed ? 0.6 : 1,
            }}
          >
            <CollapsibleNode
              label={label}
              value={val}
              depth={0}
              defaultExpanded={defaultExpanded}
            />
          </Box>
        );
      })}
    </Box>
  );
}

const sqlHighlightStyle: React.CSSProperties = {
  background: '#1e293b',
  borderRadius: '6px',
  padding: '16px',
  fontSize: '1.25rem',
  lineHeight: '1.8',
  maxHeight: 700,
  margin: 0,
};

const diffStyles = {
  variables: {
    dark: {
      diffViewerBackground: '#1e293b',
      gutterBackground: '#1e293b',
      addedBackground: '#12361e',
      removedBackground: '#3b1219',
      wordAddedBackground: '#1a4d2e',
      wordRemovedBackground: '#5c1d24',
      addedGutterBackground: '#12361e',
      removedGutterBackground: '#3b1219',
    },
  },
  contentText: {
    fontSize: '1.1rem',
    lineHeight: '1.7',
    wordBreak: 'break-all' as const,
    whiteSpace: 'pre-wrap' as const,
    fontFamily: 'monospace',
  },
  line: {
    wordBreak: 'break-all' as const,
  },
  gutter: {
    fontSize: '0.9rem',
    minWidth: '36px',
    padding: '0 6px',
  },
  titleBlock: {
    fontSize: '1rem',
    fontWeight: 700,
    padding: '8px 12px',
  },
};

interface FixtureDetailProps {
  fixture: FixtureData;
  expanded?: boolean;
  onToggle?: (id: string) => void;
}

interface BlockViewerProps {
  fixtureId: string;
  blockName: BlockName;
  label: string;
  data: unknown;
  blockHash: string;
}

function BlockViewer({ fixtureId, blockName, label, data, blockHash }: BlockViewerProps) {
  const targetId = `${fixtureId}:${blockName}`;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
          {label}
        </Typography>
        <AnnotationThread targetId={targetId} currentBlockHash={blockHash} />
      </Box>
      <CollapsibleJsonTree data={data} />
    </Box>
  );
}

type ViewMode =
  | 'blocks'
  | 'fullJson'
  | 'legacy'
  | 'compare'
  | 'sql'
  | 'sqlDiff'
  | 'validation'
  | 'dslEval';

function extractAvs(cohortDef: Record<string, unknown> | null): Record<string, unknown>[] {
  if (!cohortDef) return [];
  const avs = cohortDef.analyticalVariables;
  if (Array.isArray(avs)) return avs as Record<string, unknown>[];
  return [];
}

function LegacyIrViewer({ data }: { data: Record<string, unknown> }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} fontSize="1rem" sx={{ mb: 0.5 }}>
        Legacy Cohort Builder IR
      </Typography>
      <CollapsibleJsonTree data={data} />
    </Box>
  );
}

const LANGFUSE_STATUS_CHIP: Record<
  LangfuseEvaluationStatus,
  { color: string; bgcolor: string; label: string }
> = {
  pass: { color: 'green.800', bgcolor: 'green.50', label: 'Pass' },
  fail: { color: 'red.800', bgcolor: '#fef2f2', label: 'Fail' }, // TODO: no VDS token for bgcolor — needs designer input (Barry Richardson)
  error: { color: 'orange.900', bgcolor: 'orange.50', label: 'Error' },
  pending: { color: 'grey.600', bgcolor: 'grey.50', label: 'Pending' },
  not_in_dataset: { color: 'bluegrey.600', bgcolor: 'bluegrey.50', label: 'Not in Dataset' },
};

function DslEvalDetailView({
  evaluation,
}: {
  evaluation: NonNullable<FixtureData['langfuseEvaluation']>;
}) {
  const chip = LANGFUSE_STATUS_CHIP[evaluation.status as LangfuseEvaluationStatus];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Status banner */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: `${borderRadius.md}px`,
          bgcolor: chip.bgcolor,
          border: '1px solid',
          borderColor: chip.color,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700} color={chip.color}>
          {chip.label}
        </Typography>
        {evaluation.datasetRun && (
          <Typography variant="body1" fontFamily="monospace" color="text.secondary">
            {evaluation.datasetRun}
          </Typography>
        )}
        {evaluation.lastRunAt && (
          <Typography variant="body1" color="text.secondary">
            — {new Date(evaluation.lastRunAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      {/* Score cards */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            bgcolor: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
            borderRadius: `${borderRadius.md}px`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            AV Match Rate
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {evaluation.avMatchRate !== null ? `${Math.round(evaluation.avMatchRate * 100)}%` : '-'}
          </Typography>
          {evaluation.analyticalVariables && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Matched: {evaluation.analyticalVariables.matched}
              </Typography>
              <Typography variant="body2">
                Missing: {evaluation.analyticalVariables.missing}
              </Typography>
              <Typography variant="body2">Extra: {evaluation.analyticalVariables.extra}</Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 1.5,
            bgcolor: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
            borderRadius: `${borderRadius.md}px`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Criteria Match Rate
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {evaluation.criteriaMatchRate !== null
              ? `${Math.round(evaluation.criteriaMatchRate * 100)}%`
              : '-'}
          </Typography>
          {evaluation.criteria && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Matched: {evaluation.criteria.matched}</Typography>
              <Typography variant="body2">Missing: {evaluation.criteria.missing}</Typography>
              <Typography variant="body2">Extra: {evaluation.criteria.extra}</Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 1.5,
            bgcolor: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
            borderRadius: `${borderRadius.md}px`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Study Period
          </Typography>
          {evaluation.studyPeriod ? (
            <>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: evaluation.studyPeriod.passed ? 'green.800' : 'red.800' }}
              >
                {evaluation.studyPeriod.passed ? '\u2713 Pass' : '\u2717 Fail'}
              </Typography>
              {evaluation.studyPeriod.reason && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {evaluation.studyPeriod.reason}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="h5" fontWeight={700} color="text.secondary">
              -
            </Typography>
          )}
        </Box>
      </Box>

      {/* Score comments (failure details) */}
      {evaluation.scoreComments?.av_match_rate && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: `${borderRadius.md}px`,
            bgcolor: '#fef2f2', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
            border: '1px solid',
            borderColor: '#fecaca', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Failure Details
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap' }}>
            {evaluation.scoreComments.av_match_rate}
          </Typography>
        </Box>
      )}

      {/* Latency and metadata */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        {evaluation.latencyMs !== null && (
          <Typography variant="body1" color="text.secondary">
            Latency: {(evaluation.latencyMs / 1000).toFixed(1)}s
          </Typography>
        )}
        {evaluation.costUsd !== null && evaluation.costUsd !== undefined && (
          <Typography variant="body1" color="text.secondary">
            Cost: ${evaluation.costUsd.toFixed(4)}
          </Typography>
        )}
        {evaluation.traceId && (
          <Typography variant="body2" color="text.disabled" fontFamily="monospace">
            Trace: {evaluation.traceId.slice(0, 8)}...
          </Typography>
        )}
      </Box>
    </Box>
  );
}

type StepViewMode = 'json' | 'diff';

function ConversationStepper({ turns }: { turns: ConversationTurn[] }) {
  const [expandedStep, setExpandedStep] = useState<number>(turns.length - 1);
  const [stepViewMode, setStepViewMode] = useState<StepViewMode>('json');

  const getPrevExpectedOutput = useCallback(
    (idx: number): Record<string, unknown> | undefined => {
      for (let i = idx - 1; i >= 0; i--) {
        if (turns[i].expectedOutput) return turns[i].expectedOutput;
      }
      return undefined;
    },
    [turns]
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} fontSize="1rem" sx={{ mb: 1 }}>
        Conversation ({turns.length} turns)
      </Typography>
      <Box sx={{ position: 'relative', pl: 3 }}>
        {/* Vertical timeline line */}
        <Box
          sx={{
            position: 'absolute',
            left: 10,
            top: 8,
            bottom: 8,
            width: 2,
            bgcolor: 'grey.300',
          }}
        />
        {turns.map((turn, idx) => {
          const isExpanded = expandedStep === idx;
          const hasOutput = !!turn.expectedOutput;
          const prevOutput = getPrevExpectedOutput(idx);

          return (
            <Box key={idx} sx={{ position: 'relative', mb: 1.5 }}>
              {/* Step number badge */}
              <Box
                onClick={() => setExpandedStep(isExpanded ? -1 : idx)}
                sx={{
                  position: 'absolute',
                  left: -22,
                  top: 6,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: hasOutput ? 'cyan.800' : 'grey.400',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  zIndex: 1,
                }}
              >
                {idx + 1}
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  bgcolor: isExpanded ? '#f0fdfa' : '#f8fafc', // TODO: no VDS token for these values — needs designer input (Barry Richardson)
                  borderRadius: `${borderRadius.md}px`,
                  border: '1px solid',
                  borderColor: isExpanded ? 'cyan.800' : 'grey.200',
                  cursor: hasOutput ? 'pointer' : 'default',
                }}
                onClick={() => hasOutput && setExpandedStep(isExpanded ? -1 : idx)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'cyan.800' }}>
                    Turn {idx + 1}
                  </Typography>
                  {turn.changeSummary && (
                    <Chip
                      label={turn.changeSummary}
                      size="small"
                      sx={{
                        bgcolor: 'teal.50',
                        color: 'teal.800',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  )}
                  {hasOutput && (
                    <ChevronRightIcon
                      sx={{
                        ml: 'auto',
                        fontSize: '1.2rem',
                        color: 'grey.500',
                        transform: isExpanded ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body2">{turn.message}</Typography>

                {/* Expanded detail panel */}
                {isExpanded && hasOutput && (
                  <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                    <ToggleButtonGroup
                      value={stepViewMode}
                      exclusive
                      onChange={(_, v) => v && setStepViewMode(v as StepViewMode)}
                      size="small"
                      sx={{ mb: 1 }}
                    >
                      <ToggleButton value="json" sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}>
                        JSON
                      </ToggleButton>
                      <ToggleButton value="diff" sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}>
                        Diff
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {stepViewMode === 'json' && turn.expectedOutput && (
                      <Box
                        sx={{
                          background: '#1e293b',
                          borderRadius: '6px',
                          p: 1.5,
                          maxHeight: 400,
                          overflow: 'auto',
                        }}
                      >
                        <CollapsibleJsonTree
                          data={turn.expectedOutput}
                          defaultExpanded={false}
                          changedBlocks={turn.changedBlocks}
                        />
                      </Box>
                    )}

                    {stepViewMode === 'diff' && turn.expectedOutput && (
                      <Box
                        sx={{
                          borderRadius: '6px',
                          overflow: 'auto',
                          maxHeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        <ReactDiffViewer
                          oldValue={
                            prevOutput ? JSON.stringify(stripNulls(prevOutput), null, 2) : ''
                          }
                          newValue={JSON.stringify(stripNulls(turn.expectedOutput), null, 2)}
                          splitView={false}
                          compareMethod={DiffMethod.LINES}
                          useDarkTheme
                          leftTitle={prevOutput ? `Turn ${idx}` : '(empty)'}
                          rightTitle={`Turn ${idx + 1}`}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function FixtureDetail({ fixture, expanded, onToggle }: FixtureDetailProps) {
  const cohortDef = fixture.cohortDefinition;
  const avs = extractAvs(cohortDef);
  const legacyIr = fixture.legacyIr;
  const [viewMode, setViewMode] = useState<ViewMode>('blocks');
  const { reviews, toggleReview } = useFixtureReviews();
  const review = reviews[fixture.id];
  const isReviewed = !!review || !!fixture.reviewed;

  const [intentCopied, setIntentCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (cohortDef) {
      navigator.clipboard.writeText(JSON.stringify(stripNulls(cohortDef), null, 2));
    }
  }, [cohortDef]);

  const handleReviewToggle = useCallback(() => {
    toggleReview(fixture.id);
  }, [fixture.id, toggleReview]);

  const handleAccordionChange = useCallback(() => {
    onToggle?.(fixture.id);
  }, [fixture.id, onToggle]);

  const accordionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded && accordionRef.current) {
      accordionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expanded]);

  const accordionProps =
    expanded !== undefined ? { expanded, onChange: handleAccordionChange } : {};

  return (
    <Accordion ref={accordionRef} TransitionProps={{ unmountOnExit: true }} {...accordionProps}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.75,
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
            {/* Row 1: ID + badges */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {/* ID — click to copy */}
              <Tooltip title="Click to copy ID" placement="top">
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(fixture.id);
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    borderRadius: `${borderRadius.md}px`,
                    px: 0.5,
                    mx: -0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:hover .copy-icon': { opacity: 1 },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontFamily="monospace"
                    fontWeight={700}
                    sx={{ minWidth: 80 }}
                  >
                    {fixture.id}
                  </Typography>
                  <ContentCopyIcon
                    className="copy-icon"
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      opacity: 0,
                      transition: 'opacity 0.15s',
                    }}
                  />
                </Box>
              </Tooltip>
              {/* Status */}
              <Chip
                label={STATUS_LABELS[fixture.status] || fixture.status}
                color={STATUS_COLORS[fixture.status] || 'default'}
                size="small"
                sx={{ fontWeight: 500, fontSize: '1.25rem', px: 0.5 }}
              />
              {/* Complexity */}
              <Chip
                label={fixture.complexity.replace('_', ' ')}
                size="small"
                sx={{
                  bgcolor: COMPLEXITY_BG[fixture.complexity] || '#f5f5f5', // TODO: no VDS token for fallback — needs designer input (Barry Richardson)
                  color: COMPLEXITY_FG[fixture.complexity] || 'text.primary',
                  fontWeight: 500,
                  fontSize: '1.25rem',
                  px: 0.5,
                  border: '1px solid',
                  borderColor: COMPLEXITY_FG[fixture.complexity] || 'text.primary',
                }}
              />
              {/* Roles */}
              {fixture.roles.map((role) => (
                <Chip
                  key={role}
                  label={role.replace('_', ' ')}
                  size="small"
                  sx={{
                    bgcolor: ROLE_COLORS[role] || 'bluegrey.600',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    px: 0.5,
                  }}
                />
              ))}
              {/* Legacy indicator */}
              {legacyIr && (
                <Chip
                  label="Legacy"
                  size="small"
                  sx={{
                    bgcolor: 'amber.50',
                    color: '#f57f17', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    px: 0.5,
                    border: '1px solid',
                    borderColor: '#f57f17', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                  }}
                />
              )}
              {/* Multi-turn indicator */}
              {fixture.isMultiTurn && (
                <Chip
                  label={`Multi-turn (${fixture.numTurns})`}
                  size="small"
                  sx={{
                    bgcolor: 'cyan.50',
                    color: 'cyan.800',
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    px: 0.5,
                    border: '1px solid',
                    borderColor: 'cyan.800',
                  }}
                />
              )}
              {/* SQL status indicator */}
              {fixture.expressible &&
                (() => {
                  const hasAvSql = !!fixture.avIrSql;
                  const hasLegacySql = !!fixture.legacySql;
                  const hasAvError = !!fixture.translationError;
                  const hasLegacyError = !!fixture.legacyTranslationError;
                  const hasLegacy = !!legacyIr;
                  const bothSql = hasAvSql && (!hasLegacy || hasLegacySql);
                  const anyError = hasAvError || hasLegacyError;
                  if (bothSql && !anyError) {
                    return (
                      <Chip
                        label={hasLegacy ? 'SQL (both)' : 'SQL'}
                        size="small"
                        sx={{
                          bgcolor: '#ede9fe', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                          color: '#7c3aed', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                          fontWeight: 500,
                          fontSize: '1.25rem',
                          px: 0.5,
                          border: '1px solid',
                          borderColor: '#7c3aed', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                        }}
                      />
                    );
                  }
                  if (anyError) {
                    const label =
                      hasAvError && hasLegacyError
                        ? 'SQL Error (both)'
                        : hasAvError
                        ? 'SQL Error (AV)'
                        : 'SQL Error (Legacy)';
                    return (
                      <Chip
                        label={label}
                        size="small"
                        sx={{
                          bgcolor: '#fef2f2', // TODO: no VDS token for this value — needs designer input (Barry Richardson) // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                          color: '#dc2626', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                          fontWeight: 500,
                          fontSize: '0.8125rem',
                          px: 0.5,
                          border: '1px solid',
                          borderColor: '#dc2626', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                        }}
                      />
                    );
                  }
                  return null;
                })()}
              {/* Snowflake validation chip */}
              {fixture.snowflakeValidation &&
                fixture.snowflakeValidation.status !== 'pending' &&
                (() => {
                  const chip =
                    VALIDATION_STATUS_CHIP[
                      fixture.snowflakeValidation.status as SnowflakeValidationStatus
                    ];
                  return (
                    <Chip
                      label={`SF: ${chip.label}`}
                      size="small"
                      sx={{
                        bgcolor: chip.bgcolor,
                        color: chip.color,
                        fontWeight: 500,
                        fontSize: '1.25rem',
                        px: 0.5,
                        border: '1px solid',
                        borderColor: chip.color,
                      }}
                    />
                  );
                })()}
            </Box>
            {/* Row 2: Intent — full width, clear of badges, click to copy */}
            <Tooltip title={intentCopied ? 'Copied!' : 'Click to copy intent'} followCursor>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(fixture.intent);
                  setIntentCopied(true);
                  setTimeout(() => setIntentCopied(false), 1500);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  cursor: 'pointer',
                  borderRadius: `${borderRadius.md}px`,
                  px: 0.5,
                  mx: -0.5,
                  mt: 0.75,
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:hover .copy-icon': { opacity: 1 },
                }}
              >
                <Typography
                  variant="body1"
                  color="text.primary"
                  sx={{ fontSize: '1.5rem', fontWeight: 400, lineHeight: 1.5 }}
                >
                  {fixture.intent}
                </Typography>
                {intentCopied ? (
                  <CheckCircleIcon
                    sx={{
                      fontSize: 16,
                      color: 'success.main',
                      mt: 0.75,
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <ContentCopyIcon
                    className="copy-icon"
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      opacity: 0,
                      transition: 'opacity 0.15s',
                      mt: 0.75,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          </Box>
          {/* Review indicator */}
          {isReviewed && (
            <Tooltip
              title={
                review
                  ? `Reviewed by ${review.reviewedByName} on ${new Date(
                      review.reviewedAt
                    ).toLocaleDateString()}`
                  : 'Reviewed'
              }
            >
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, ml: 'auto' }} />
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* Primitives — top of expanded content */}
        {fixture.primitives.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
                Primitives
              </Typography>
              <AnnotationThread
                targetId={`${fixture.id}:primitives`}
                currentBlockHash={fixture.primitives.join(',')}
              />
            </Box>
            <Typography
              variant="body1"
              fontFamily="monospace"
              color="text.primary"
              sx={{ fontSize: '0.9375rem' }}
            >
              {fixture.primitives.join(', ')}
            </Typography>
          </Box>
        )}

        {/* Tags */}
        {fixture.tags.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} fontSize="1rem" sx={{ mb: 0.5 }}>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {fixture.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: '#f5f5f5', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                    color: 'text.primary',
                    fontWeight: 500,
                    fontSize: '1rem',
                    border: '1px solid',
                    borderColor: '#f57f17', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Multi-turn conversation stepper */}
        {fixture.isMultiTurn && fixture.turns && fixture.turns.length > 0 && (
          <ConversationStepper turns={fixture.turns} />
        )}

        {/* Review toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.5,
            bgcolor: isReviewed ? 'green.50' : 'grey.50',
            borderRadius: `${borderRadius.md}px`,
            border: '1px solid',
            borderColor: isReviewed ? 'success.light' : 'grey.200',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox checked={isReviewed} onChange={handleReviewToggle} color="success" />
            }
            label={
              review ? (
                <Typography variant="body2" fontWeight={600} color="success.dark">
                  Reviewed by {review.reviewedByName} on{' '}
                  {new Date(review.reviewedAt).toLocaleDateString()}
                </Typography>
              ) : fixture.reviewed ? (
                <Typography variant="body2" fontWeight={600} color="success.dark">
                  Reviewed
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Mark as reviewed
                </Typography>
              )
            }
          />
        </Box>

        {/* View mode toggle */}
        {cohortDef && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, val: ViewMode | null) => {
                if (val) setViewMode(val);
              }}
              size="small"
            >
              <ToggleButton value="blocks">AV Blocks</ToggleButton>
              <ToggleButton value="fullJson">Full JSON</ToggleButton>
              {legacyIr && <ToggleButton value="legacy">Legacy CB</ToggleButton>}
              {legacyIr && <ToggleButton value="compare">Compare</ToggleButton>}
              <ToggleButton value="sql">SQL</ToggleButton>
              {legacyIr && <ToggleButton value="sqlDiff">SQL Diff</ToggleButton>}
              {fixture.snowflakeValidation && (
                <ToggleButton value="validation">Validation</ToggleButton>
              )}
              {fixture.langfuseEvaluation && <ToggleButton value="dslEval">DSL Eval</ToggleButton>}
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Compare mode: diff viewer */}
        {viewMode === 'compare' && cohortDef && legacyIr && (
          <Box sx={{ overflow: 'auto', maxWidth: '100%' }}>
            <ReactDiffViewer
              oldValue={JSON.stringify(stripNulls(legacyIr), null, 2)}
              newValue={JSON.stringify(stripNulls(cohortDef), null, 2)}
              splitView={true}
              leftTitle="Legacy Cohort Builder IR"
              rightTitle="Cohort Definition"
              useDarkTheme={true}
              compareMethod={DiffMethod.LINES}
              styles={diffStyles}
            />
          </Box>
        )}

        {/* SQL view */}
        {viewMode === 'sql' && (
          <Box sx={{ display: 'flex', gap: 2, flexDirection: legacyIr ? 'row' : 'column' }}>
            {/* AV IR SQL panel */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {fixture.avIrSql ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
                      Generated SQL (AV IR)
                    </Typography>
                    <Tooltip title="Copy SQL">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (fixture.avIrSql) {
                            navigator.clipboard.writeText(fixture.avIrSql);
                          }
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <SyntaxHighlighter
                    language="sql"
                    style={a11yDark}
                    customStyle={sqlHighlightStyle}
                    showLineNumbers
                    lineNumberStyle={{
                      color:
                        '#4a5568' /* TODO: no VDS token for this value — needs designer input (Barry Richardson) */,
                      fontSize: '0.85rem',
                      minWidth: '2.5em',
                    }}
                    wrapLongLines={false}
                  >
                    {fixture.avIrSql}
                  </SyntaxHighlighter>
                </>
              ) : fixture.translationError ? (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#fef2f2', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                    borderRadius: `${borderRadius.md}px`,
                    border: '1px solid #fecaca', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                  }}
                >
                  <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                    AV IR Translation Error
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}
                  >
                    {fixture.translationError}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No SQL generated
                </Typography>
              )}
            </Box>

            {/* Legacy SQL panel — side by side when legacy exists */}
            {legacyIr && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {fixture.legacySql ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
                        Generated SQL (Legacy)
                      </Typography>
                      <Tooltip title="Copy Legacy SQL">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (fixture.legacySql) {
                              navigator.clipboard.writeText(fixture.legacySql);
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <SyntaxHighlighter
                      language="sql"
                      style={a11yDark}
                      customStyle={sqlHighlightStyle}
                      showLineNumbers
                      lineNumberStyle={{
                        color:
                          '#4a5568' /* TODO: no VDS token for this value — needs designer input (Barry Richardson) */,
                        fontSize: '0.85rem',
                        minWidth: '2.5em',
                      }}
                      wrapLongLines={false}
                    >
                      {fixture.legacySql}
                    </SyntaxHighlighter>
                  </>
                ) : fixture.legacyTranslationError ? (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#fffbeb', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                      borderRadius: `${borderRadius.md}px`,
                      border: '1px solid #fde68a', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.5,
                        color:
                          '#92400e' /* TODO: no VDS token for this value — needs designer input (Barry Richardson) */,
                      }}
                    >
                      Legacy Translation Error
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        color:
                          '#78350f' /* TODO: no VDS token for this value — needs designer input (Barry Richardson) */,
                      }}
                    >
                      {fixture.legacyTranslationError}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
        )}

        {/* SQL Diff view */}
        {viewMode === 'sqlDiff' && legacyIr && (
          <>
            {fixture.avIrSql && fixture.legacySql ? (
              <Box sx={{ overflow: 'auto', maxWidth: '100%' }}>
                <ReactDiffViewer
                  oldValue={fixture.legacySql}
                  newValue={fixture.avIrSql}
                  splitView={true}
                  leftTitle="Legacy DSL SQL"
                  rightTitle="AV IR SQL"
                  useDarkTheme={true}
                  compareMethod={DiffMethod.WORDS}
                  styles={diffStyles}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                {!fixture.avIrSql && fixture.translationError && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#fef2f2', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                      borderRadius: `${borderRadius.md}px`,
                      border: '1px solid #fecaca', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                    }}
                  >
                    <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                      AV IR Translation Error
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}
                    >
                      {fixture.translationError}
                    </Typography>
                  </Box>
                )}
                {!fixture.legacySql && fixture.legacyTranslationError && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#fffbeb', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                      borderRadius: `${borderRadius.md}px`,
                      border: '1px solid #fde68a', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.5,
                        color:
                          '#92400e' /* TODO: no VDS token for this value — needs designer input (Barry Richardson) */,
                      }}
                    >
                      Legacy Translation Error
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        color:
                          '#78350f' /* TODO: no VDS token for this value — needs designer input (Barry Richardson) */,
                      }}
                    >
                      {fixture.legacyTranslationError}
                    </Typography>
                  </Box>
                )}
                {!fixture.translationError && !fixture.legacyTranslationError && (
                  <Typography variant="body2" color="text.secondary">
                    SQL diff unavailable — both translations must succeed
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}

        {/* Validation view */}
        {viewMode === 'validation' && fixture.snowflakeValidation && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Status banner */}
            {(() => {
              const v = fixture.snowflakeValidation;
              const chip = VALIDATION_STATUS_CHIP[v.status as SnowflakeValidationStatus];
              return (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: `${borderRadius.md}px`,
                    bgcolor: chip.bgcolor,
                    border: '1px solid',
                    borderColor: chip.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={chip.color}>
                    {chip.label}
                  </Typography>
                  {v.dataset && (
                    <Typography variant="body1" fontFamily="monospace" color="text.secondary">
                      {v.dataset}
                    </Typography>
                  )}
                  {v.lastRunAt && (
                    <Typography variant="body1" color="text.secondary">
                      — {new Date(v.lastRunAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              );
            })()}

            {/* SQL modifications notice */}
            {fixture.snowflakeValidation.sqlModifications &&
              fixture.snowflakeValidation.sqlModifications.length > 0 && (
                <NotesLink
                  title={`${fixture.id} — SQL Modifications`}
                  notes={fixture.snowflakeValidation.sqlModifications}
                />
              )}

            {/* Execution results — two column */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  p: 1.5,
                  bgcolor: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                  borderRadius: `${borderRadius.md}px`,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  AV IR Execution
                </Typography>
                {fixture.snowflakeValidation.avIr ? (
                  <>
                    <Typography variant="body1">
                      Row count: <strong>{fixture.snowflakeValidation.avIr.rowCount}</strong>
                    </Typography>
                    <Typography variant="body1">
                      Execution time:{' '}
                      <strong>
                        {formatExecutionTime(fixture.snowflakeValidation.avIr.executionTimeMs)}
                      </strong>
                      {fixture.snowflakeValidation.legacy &&
                        (() => {
                          const delta = formatTimeDelta(
                            fixture.snowflakeValidation!.avIr!.executionTimeMs,
                            fixture.snowflakeValidation!.legacy!.executionTimeMs
                          );
                          return (
                            <Typography
                              component="span"
                              sx={{ ml: 0.5, color: delta.color, fontWeight: 600 }}
                            >
                              {delta.text}
                            </Typography>
                          );
                        })()}
                    </Typography>
                    {fixture.snowflakeValidation.avIr.error && (
                      <Typography variant="body1" color="error" sx={{ mt: 0.5 }}>
                        {fixture.snowflakeValidation.avIr.error}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No AV IR execution
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 1.5,
                  bgcolor: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                  borderRadius: `${borderRadius.md}px`,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Legacy Execution
                </Typography>
                {fixture.snowflakeValidation.legacy ? (
                  <>
                    <Typography variant="body1">
                      Row count: <strong>{fixture.snowflakeValidation.legacy.rowCount}</strong>
                    </Typography>
                    <Typography variant="body1">
                      Execution time:{' '}
                      <strong>
                        {formatExecutionTime(fixture.snowflakeValidation.legacy.executionTimeMs)}
                      </strong>
                    </Typography>
                    {fixture.snowflakeValidation.legacy.error && (
                      <Typography variant="body1" color="error" sx={{ mt: 0.5 }}>
                        {fixture.snowflakeValidation.legacy.error}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No legacy SQL
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Comparison section */}
            {fixture.snowflakeValidation.comparison && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: '#f8fafc', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                  borderRadius: `${borderRadius.md}px`,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Comparison
                </Typography>
                <Typography variant="body1">
                  Row count match:{' '}
                  {fixture.snowflakeValidation.comparison.rowCountMatch ? (
                    <Typography component="span" color="success.main" fontWeight={600}>
                      {'\u2713'} Yes
                    </Typography>
                  ) : (
                    <Typography component="span" color="error.main" fontWeight={600}>
                      {'\u2717'} No
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body1">
                  Person ID set match:{' '}
                  {fixture.snowflakeValidation.comparison.personIdSetMatch ? (
                    <Typography component="span" color="success.main" fontWeight={600}>
                      {'\u2713'} Yes
                    </Typography>
                  ) : (
                    <Typography component="span" color="error.main" fontWeight={600}>
                      {'\u2717'} No
                    </Typography>
                  )}
                </Typography>
                {!fixture.snowflakeValidation.comparison.personIdSetMatch && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1">
                      AV only: {fixture.snowflakeValidation.comparison.avOnlyCount} person IDs
                    </Typography>
                    <Typography variant="body1">
                      Legacy only: {fixture.snowflakeValidation.comparison.legacyOnlyCount} person
                      IDs
                    </Typography>
                    {fixture.snowflakeValidation.comparison.sampleAvOnly.length > 0 && (
                      <Typography
                        variant="body1"
                        fontFamily="monospace"
                        sx={{ fontSize: '1rem', mt: 0.5 }}
                      >
                        Sample AV only: [
                        {fixture.snowflakeValidation.comparison.sampleAvOnly.join(', ')}]
                      </Typography>
                    )}
                    {fixture.snowflakeValidation.comparison.sampleLegacyOnly.length > 0 && (
                      <Typography variant="body1" fontFamily="monospace" sx={{ fontSize: '1rem' }}>
                        Sample Legacy only: [
                        {fixture.snowflakeValidation.comparison.sampleLegacyOnly.join(', ')}]
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Failure detail */}
            {fixture.snowflakeValidation.failureDetail && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: '#fef2f2',
                  borderRadius: `${borderRadius.md}px`,
                  border: '1px solid #fecaca',
                }} // TODO: no VDS token for bgcolor/border color — needs designer input (Barry Richardson)
              >
                <Typography variant="subtitle1" color="error" sx={{ mb: 0.5 }}>
                  Failure Detail
                </Typography>
                <Typography
                  variant="body1"
                  fontFamily="monospace"
                  sx={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}
                >
                  {fixture.snowflakeValidation.failureDetail.message}
                </Typography>
              </Box>
            )}

            {/* Skip reason */}
            {fixture.snowflakeValidation.skipReason && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'bluegrey.50',
                  borderRadius: `${borderRadius.md}px`,
                  border: '1px solid',
                  borderColor: 'bluegrey.300',
                }}
              >
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                  Skip Reason
                </Typography>
                <Typography variant="body1">{fixture.snowflakeValidation.skipReason}</Typography>
              </Box>
            )}
            {/* Executed SQL panels */}
            {(fixture.snowflakeValidation.executedAvIrSql ||
              fixture.snowflakeValidation.executedLegacySql) && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {fixture.snowflakeValidation.executedAvIrSql && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
                        Executed SQL (AV IR)
                      </Typography>
                      <Tooltip title="Copy SQL">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (fixture.snowflakeValidation?.executedAvIrSql) {
                              navigator.clipboard.writeText(
                                fixture.snowflakeValidation.executedAvIrSql
                              );
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <SyntaxHighlighter
                      language="sql"
                      style={a11yDark}
                      customStyle={sqlHighlightStyle}
                      showLineNumbers
                      lineNumberStyle={{
                        color: '#4a5568', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                        fontSize: '0.85rem',
                        minWidth: '2.5em',
                      }}
                      wrapLongLines={false}
                    >
                      {fixture.snowflakeValidation.executedAvIrSql}
                    </SyntaxHighlighter>
                  </Box>
                )}
                {fixture.snowflakeValidation.executedLegacySql && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
                        Executed SQL (Legacy)
                      </Typography>
                      <Tooltip title="Copy SQL">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (fixture.snowflakeValidation?.executedLegacySql) {
                              navigator.clipboard.writeText(
                                fixture.snowflakeValidation.executedLegacySql
                              );
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <SyntaxHighlighter
                      language="sql"
                      style={a11yDark}
                      customStyle={sqlHighlightStyle}
                      showLineNumbers
                      lineNumberStyle={{
                        color: '#4a5568', // TODO: no VDS token for this value — needs designer input (Barry Richardson)
                        fontSize: '0.85rem',
                        minWidth: '2.5em',
                      }}
                      wrapLongLines={false}
                    >
                      {fixture.snowflakeValidation.executedLegacySql}
                    </SyntaxHighlighter>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* DSL Eval view */}
        {viewMode === 'dslEval' && fixture.langfuseEvaluation && (
          <DslEvalDetailView evaluation={fixture.langfuseEvaluation} />
        )}

        {/* Cohort Definition — AV block view */}
        {cohortDef && viewMode === 'blocks' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1">Cohort Definition</Typography>
              <Tooltip title="Copy full JSON">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {avs.map((av, i) => {
              const prefix = avs.length > 1 ? `av${i}:` : '';
              return (
                <Box key={i} sx={{ mb: 2 }}>
                  {avs.length > 1 && (
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      {String(av.name || `AV ${i + 1}`)}
                    </Typography>
                  )}
                  {'logic' in av && av.logic != null && (
                    <BlockViewer
                      fixtureId={fixture.id}
                      blockName={`${prefix}logic`}
                      label="Logic"
                      data={av.logic}
                      blockHash={fixture.blockHashes[`${prefix}logic`] || ''}
                    />
                  )}
                  {'window' in av && av.window != null && (
                    <BlockViewer
                      fixtureId={fixture.id}
                      blockName={`${prefix}window`}
                      label="Window"
                      data={av.window}
                      blockHash={fixture.blockHashes[`${prefix}window`] || ''}
                    />
                  )}
                  {'aggregation' in av && av.aggregation != null && (
                    <BlockViewer
                      fixtureId={fixture.id}
                      blockName={`${prefix}aggregation`}
                      label="Aggregation"
                      data={av.aggregation}
                      blockHash={fixture.blockHashes[`${prefix}aggregation`] || ''}
                    />
                  )}
                  {'output' in av && av.output != null && (
                    <BlockViewer
                      fixtureId={fixture.id}
                      blockName={`${prefix}output`}
                      label="Output"
                      data={av.output}
                      blockHash={fixture.blockHashes[`${prefix}output`] || ''}
                    />
                  )}
                </Box>
              );
            })}
          </>
        )}

        {/* Cohort Definition — full JSON view */}
        {cohortDef && viewMode === 'fullJson' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
                Cohort Definition
              </Typography>
              <Tooltip title="Copy full JSON">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <CollapsibleJsonTree data={cohortDef} />
          </Box>
        )}

        {/* Legacy IR — single column view */}
        {legacyIr && viewMode === 'legacy' && <LegacyIrViewer data={legacyIr} />}

        {/* Gap Notes */}
        {fixture.gapNotes && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2">Gap Notes</Typography>
              <AnnotationThread
                targetId={`${fixture.id}:gapNotes`}
                currentBlockHash={fixture.gapNotes}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {fixture.gapNotes}
            </Typography>
          </Box>
        )}

        {/* Not expressible label */}
        {!cohortDef && !fixture.gapNotes && (
          <Typography variant="body2" color="text.secondary">
            Not expressible — no IR available
          </Typography>
        )}

        {/* Validation error */}
        {fixture.validationError && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="error">
              Validation Error
            </Typography>
            <Typography
              variant="body2"
              fontFamily="monospace"
              sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}
            >
              {fixture.validationError}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
